import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import API from "util/API";

import { showErrorSnackbar } from "store/actions/snackbarMessage";
import { checkMobilePhone } from "util/orderUtils";
import RestaurantHeader from "./RestaurantHeader";
import useRestaurantInfo from "./useRestaurantInfo";
import {
  MAX_GUESTS,
  isQueueClosed,
  isQueueFormValid,
  normalizePhone,
  queueStatusPath,
} from "./queueFormUtils";

const initialState = {
  name: "",
  guestNum: 2,
  phoneNumber: "",
};

// Resolves to a reCAPTCHA token, or null when the script never loaded (ad
// blockers, flaky networks); the server decides what to do without one.
const getRecaptchaToken = (captchaKey) =>
  new Promise((resolve) => {
    if (!window.grecaptcha || !captchaKey) {
      resolve(null);
      return;
    }
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaKey, { action: "submit" })
        .then(resolve, () => resolve(null));
    });
  });

const QueueForm = (props) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initialState);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closedOnSubmit, setClosedOnSubmit] = useState(false);
  const history = useHistory();

  const showError = (msg) => dispatch(showErrorSnackbar(msg));

  const restaurantId = props.match.params.restaurantId;
  const captchaKey = import.meta.env.VITE_APP_RECAPTCHA;

  useEffect(() => {
    if (!captchaKey) return undefined;
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${captchaKey}`;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [captchaKey]);

  const { data: restaurant, isLoading: loadingRestaurant, error: restaurantError } =
    useRestaurantInfo(restaurantId);

  useEffect(() => {
    if (restaurantError) {
      console.error("queue restaurantInfo failed", restaurantError);
      showError("Something went wrong, please try it later.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const changeGuests = (delta) => {
    const next = Math.min(MAX_GUESTS, Math.max(1, Number(values.guestNum || 0) + delta));
    setValues({ ...values, guestNum: next });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!isQueueFormValid(values) || submitting) return;
    setSubmitting(true);
    try {
      const recaptcha = await getRecaptchaToken(captchaKey);
      const response = await API.post(`/queuePosition`, {
        recaptcha,
        name: values.name.trim(),
        guestNum: Number(values.guestNum),
        phoneNumber: normalizePhone(values.phoneNumber),
        restaurantId,
      });
      if (response && response.data && response.data.reference) {
        // Replace, so Back doesn't land on the form and join twice.
        history.replace(queueStatusPath(restaurantId, response.data.reference));
      } else {
        showError("Something went wrong, please try it later.");
      }
    } catch (err) {
      console.error("queuePosition failed", err);
      if (err && err.response && err.response.data && err.response.data.name === "queueClosed") {
        setClosedOnSubmit(true);
        return;
      }
      showError("Something went wrong, please try it later.");
    } finally {
      setSubmitting(false);
    }
  };

  const phoneInvalid = phoneTouched && !checkMobilePhone(normalizePhone(values.phoneNumber));

  return (
    <Box sx={{ px: 2, py: { xs: 3, sm: 6 }, display: "flex", justifyContent: "center" }}>
      <Card sx={{ width: "100%", maxWidth: 480 }} elevation={2}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {loadingRestaurant ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <RestaurantHeader restaurant={restaurant} />
          )}

          {!loadingRestaurant && (closedOnSubmit || isQueueClosed(restaurant)) ? (
            <Alert severity="info">
              <AlertTitle>The online queue is closed</AlertTitle>
              We&rsquo;re not taking queue requests online right now. Please speak to our staff.
            </Alert>
          ) : (
            <Box component="form" onSubmit={submitForm} noValidate>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Join the queue
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add your details and we&rsquo;ll hold your place in line.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Name"
                  name="name"
                  autoComplete="name"
                  value={values.name}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  required
                  label="Mobile number"
                  name="phoneNumber"
                  type="tel"
                  autoComplete="tel"
                  inputProps={{ inputMode: "tel" }}
                  error={phoneInvalid}
                  helperText={phoneInvalid ? "The mobile number is invalid" : " "}
                  value={values.phoneNumber}
                  onChange={handleInputChange}
                  onBlur={() => setPhoneTouched(true)}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Number of guests
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton
                      aria-label="fewer guests"
                      onClick={() => changeGuests(-1)}
                      disabled={Number(values.guestNum) <= 1}
                      sx={{ border: 1, borderColor: "divider" }}
                      size="large"
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography
                      variant="h4"
                      component="output"
                      aria-live="polite"
                      sx={{ minWidth: 48, textAlign: "center" }}
                    >
                      {values.guestNum}
                    </Typography>
                    <IconButton
                      aria-label="more guests"
                      onClick={() => changeGuests(1)}
                      disabled={Number(values.guestNum) >= MAX_GUESTS}
                      sx={{ border: 1, borderColor: "divider" }}
                      size="large"
                    >
                      <AddIcon />
                    </IconButton>
                  </Stack>
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  color="primary"
                  disabled={!isQueueFormValid(values) || submitting}
                  sx={{ py: 1.5 }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : "Join queue"}
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

QueueForm.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ restaurantId: PropTypes.string }),
  }).isRequired,
};

export default QueueForm;
